import React, { useState } from "react";
import { colors, fonts, space, radius } from "../theme";

const HELP_CONTENT = {
  dashboard: {
    title: "Dashboard Help",
    body: [
      "Orders: the number of orders recorded, including sample orders logged from Reports and real orders closed from the Waiter screen.",
      "Staff: how many active staff members you've added.",
      "Tables: how many active tables you've set up.",
      "Days remaining: how much time remains before your account needs to be renewed by Alvna.",
      "Staff Login Links: share these with your waiters and kitchen staff. Each link is specific to your restaurant, so they'll always log in to the right place.",
      "Print QR codes: opens one printable page with a QR code for the Waiter, Kitchen and Manager logins. Staff scan it with their phone camera to reach the right login. Untick any code you don't want on the page (for example, keep the Manager code off a wall customers can see).",
    ],
  },
  menu: {
    title: "Menu Help",
    body: [
      "Add a category first (e.g. Appetizers, Mains, Drinks), then add items inside it with a name and price.",
      "Your account has limits on how many categories and how many items per category you can add. Once you hit a limit, the Add button disables and shows the limit. Contact Alvna to raise these limits.",
      "Deleting a category also deletes every item inside it. You'll be asked to confirm first.",
      "Items you add here appear on the Waiter ordering screen automatically.",
    ],
  },
  tables: {
    title: "Tables Help",
    body: [
      "Add every real table in your restaurant here, with a label (e.g. 'Table 1', 'Patio A') and seat count.",
      "Your account has a limit on the total number of tables. The Add button disables once you hit it.",
      "Tables you add here appear on the Waiter screen, where a table shows as Occupied once an order is open on it, and Available again once that order is closed.",
    ],
  },
  onlineOrdering: {
    title: "Online Ordering Help",
    body: [
      "Share your ordering link or print its QR code. Customers browse your menu, choose ASAP or a pickup time, and can track their order by order number or phone number.",
      "How customers pay is YOUR setting (Alvna controls which options are available to you). Pay at the store: the order waits under Pending payment on the Kitchen and Waiter screens (Online orders tab). Call the customer, take payment, press Confirm payment, and it then goes to the kitchen. Orders up to your limit can also be sent to the kitchen to be paid at pickup. Card online (Stripe): customers pay when they order and paid orders go to the kitchen automatically.",
      "Connect Stripe once (test and live are separate). Money goes straight into your own Stripe account; refunds for cancelled card orders are sent back automatically.",
      "Sold-out items: switch them off in your menu and they disappear from the online menu. Turn off “Accepting online orders” any time you are too busy.",
    ],
  },
  employees: {
    title: "Employees Help",
    body: [
      "Employees are the people who clock in and out. They are separate from Staff (the waiter / kitchen / manager PIN logins), although you can link an employee to one staff account.",
      "Give each employee a 4 to 6 digit clock-in PIN (unique in your restaurant). They type their name and PIN on the clock-in page. Share the link, or print the QR code, and put it by the door or the till.",
      "Link to a staff account (optional): when “Require clock-in” is on (Attendance page), that staff account is view-only while the employee is clocked out. Choose “same PIN” if you want one number for both the staff login and the clock.",
      "Your plan has a limit on active employees. Deactivate someone who leaves and their hours are kept. An employee with recorded hours can't be deleted.",
    ],
  },
  attendance: {
    title: "Attendance Help",
    body: [
      "Who's in now shows everyone currently clocked in. History goes back up to 10 days, with each shift's photo, location check and any flags.",
      "Rules: GPS check, Enforce (must be at the restaurant), Warn (allowed but flagged) or Off. Set your restaurant location first under Settings. “Require clock-in” makes linked staff accounts view-only while clocked out. Shifts left open are closed automatically after the hours you set, and flagged.",
      "You can add, edit or close a shift, but every change needs a reason and is recorded in the change history.",
      "Photos and hours stay visible for the periods shown on this page (set by Alvna). Older records are moved to an archive that Alvna can restore, so nothing disappears without notice. Payroll export downloads a CSV (Detail: one row per shift; Summary: one row per employee) in your restaurant's time zone.",
    ],
  },
  staff: {
    title: "Staff Help",
    body: [
      "Add each person who'll use the Waiter or Kitchen screens, with a name, role, and a 4-digit PIN.",
      "The PIN is what they'll type in on the Waiter/Kitchen login screen. Share that link from your Dashboard with them.",
      "Shift Start/End (optional): if set, that PIN automatically stops working outside those hours, with no manual action needed each day. Leave both blank for no time restriction.",
      "Edit Shift anytime to adjust hours for a longer day, or leave it blank again to remove the restriction. Use Deactivate for a full day off instead of editing the shift.",
      "Deactivate a staff member if they leave, without losing their name from your records. Remove deletes them entirely.",
      "Your plan includes a set number of active staff accounts (shown at the top of this page). Deactivating someone frees a spot; once you reach the limit the Add button disables. Request more under Settings.",
    ],
  },
  reports: {
    title: "Reports Help",
    body: [
      "Shows every closed order within your retention window (set by Alvna admin). Older records are hidden from view here, but nothing is ever deleted.",
      "Record a sample order: a quick way to generate test data if you want to see Reports and your Dashboard's order count working without going through a full Waiter order.",
      "Export CSV downloads everything currently shown here as a spreadsheet file.",
    ],
  },
  waiter: {
    title: "Waiter Help",
    body: [
      "Tap a table to start or continue an order. Occupied tables (shown in amber) already have an open order.",
      "Party Size is required before sending the first order to the kitchen for a table. You won't be asked again once that table's order is open.",
      "Tap a menu item to set its quantity and add any notes (e.g. 'no onions'), then Add to Order.",
      "You can keep adding items to the same open order. Just tap 'Add More Items' from the order view.",
      "An item can only be removed while it's still 'new'. Once the kitchen has started on it, it can't be removed from here.",
      "Close Order & Print Receipt finalizes the bill, calculates tax and gratuity, and frees up the table.",
    ],
  },
  kitchen: {
    title: "Kitchen Help",
    body: [
      "Tickets are grouped by table and update automatically every few seconds, so there's no need to refresh.",
      "Tap an item to advance it: New → Preparing → Ready. Once an item is Ready, it fades and can't be tapped again.",
      "A ticket disappears once every item on it is marked Ready and the waiter closes that order.",
    ],
  },
  settings: {
    title: "Settings Help",
    body: [
      "Restaurant Profile: your business name, phone and street address are printed at the top of every receipt. Leave the street address blank to print just the name and phone.",
      "Restaurant Location (GPS): tap 'Use my current location' while you're standing in the restaurant, check the numbers, then save. The radius is how far from that point counts as at the restaurant.",
      "Time Zone: set this to wherever your restaurant actually is. Staff shift schedules (set on the Staff page) are checked against this, so get it right or shift times may block staff at the wrong hours.",
      "Request a Limit Increase: need more categories, items, tables, staff accounts, retention days, or Split Bill turned on? Submit a request here. Alvna is notified immediately and will follow up.",
      "Set your own Tax Rate and Gratuity Rate here. These are automatically applied when a waiter closes an order.",
      "Minimum Party Size for Auto-Gratuity: leave at 0 to always apply gratuity, or set a number to only auto-apply it for parties that size or larger. Once set, the waiter must enter a Party Size before closing any order.",
      "Split Bill (Equal/Custom/Full splitting) is a feature Alvna turns on per account. You can see whether it's on here, but changing it requires contacting Alvna support.",
      "These settings are reachable both from your main login and from the Manager PIN login, so floor managers can adjust them without needing your account password.",
    ],
  },
};

export default function HelpButton({ page, dark = false }) {
  const [open, setOpen] = useState(false);
  const content = HELP_CONTENT[page];
  if (!content) return null;

  return (
    <>
      <button
        className="icon-btn help-btn"
        onClick={() => setOpen(true)}
        style={dark ? styles.buttonDark : styles.button}
        title="Help"
      >
        ?
      </button>
      {open && (
        <div style={styles.overlay} onClick={() => setOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.header}>
              <strong style={{ fontFamily: fonts.display }}>{content.title}</strong>
              <button style={styles.closeBtn} onClick={() => setOpen(false)}>×</button>
            </div>
            <ul style={styles.list}>
              {content.body.map((line, i) => (
                <li key={i} style={styles.listItem}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  button: {
    width: 26, height: 26, borderRadius: radius.pill, border: `1px solid ${colors.border}`,
    background: colors.surfaceMuted, color: colors.textSecondary, fontSize: 13, fontWeight: 700,
    cursor: "pointer", marginLeft: space[2],
  },
  buttonDark: {
    width: 26, height: 26, borderRadius: radius.pill, border: `1px solid ${colors.darkBorder}`,
    background: colors.darkSurface, color: colors.darkText, fontSize: 13, fontWeight: 700,
    cursor: "pointer", marginLeft: space[2],
  },
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: { width: 380, maxWidth: "calc(100vw - 24px)", maxHeight: "70vh", overflowY: "auto", background: colors.surface, borderRadius: radius.md },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: `${space[3]} ${space[4]}`, borderBottom: `1px solid ${colors.border}`,
    position: "sticky", top: 0, background: colors.surface,
  },
  closeBtn: { background: "none", border: "none", fontSize: 20, cursor: "pointer" },
  list: { padding: `${space[3]} ${space[4]} ${space[4]} ${space[6]}`, margin: 0, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary, lineHeight: 1.6 },
  listItem: { marginBottom: space[2] },
};
